import { useState } from "react";
import axios from "axios";
import { API } from "../config";
import { useLanguage } from "../contexts/LanguageContext";
import {
  TrendingUp, Search, Eye, CheckCircle, X, Loader2, Globe,
  Zap, ArrowRight, Share2
} from "lucide-react";

const ENGINE_STYLES = {
  chatgpt: { name: "ChatGPT", color: "#10A37F", gradient: "from-emerald-900/40 to-emerald-950/20" },
  perplexity: { name: "Perplexity", color: "#20808D", gradient: "from-teal-900/40 to-teal-950/20" },
  gemini: { name: "Gemini", color: "#4285F4", gradient: "from-blue-900/40 to-blue-950/20" },
};

export default function AISimulatorPage() {
  const { t, language } = useLanguage();
  const [query, setQuery] = useState("");
  const [url, setUrl] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSimulate = async () => {
    if (!query.trim()) return;
    setLoading(true);
    setResult(null);
    try {
      const r = await axios.post(`${API}/ai-simulator`, {
        query: query.trim(),
        url: url.trim(),
        language: language || "en",
      });
      setResult(r.data);
    } catch { setResult(null); }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[var(--color-bg)] text-white">
      <header className="bg-[var(--color-bg)]/60 backdrop-blur-xl border-b border-white/10 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#a855f7] flex items-center justify-center"><TrendingUp className="w-5 h-5 text-white" /></div>
            <span className="text-xl font-black font-['Cabinet_Grotesk']">TopRankPulse</span>
          </a>
          <div className="flex items-center gap-4">
            <a href="/" className="text-sm text-[var(--color-text-muted)] hover:text-[var(--color-text)] font-['JetBrains_Mono'] uppercase">{t.nav_home || "Home"}</a>
            <a href="/brand-monitor" className="text-sm text-[var(--color-text-muted)] hover:text-[var(--color-text)] font-['JetBrains_Mono'] uppercase">{t.nav_monitor || "AI Monitor"}</a>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="py-20 px-6" data-testid="simulator-hero">
        <div className="max-w-3xl mx-auto text-center">
          <div className="flex items-center justify-center gap-2 mb-6">
            <Search className="w-5 h-5 text-[#a855f7]" />
            <span className="text-[#a855f7] font-['JetBrains_Mono'] text-sm tracking-[0.2em] uppercase">{t.sim_free || "Free Tool"}</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black font-['Cabinet_Grotesk'] tracking-tighter leading-[0.9] mb-6" data-testid="simulator-title">
            {t.sim_title_1 || "AI SEARCH"}<br /><span className="text-[#a855f7]">{t.sim_title_2 || "SIMULATOR"}</span>
          </h1>
          <p className="text-lg text-[var(--color-text-muted)] mb-10 max-w-xl mx-auto">
            {t.sim_desc || "See exactly what ChatGPT, Perplexity & Gemini would answer. Check if your website appears in AI results."}
          </p>

          {/* Input Form */}
          <div className="max-w-2xl mx-auto space-y-3">
            <input
              data-testid="simulator-query"
              type="text" value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") handleSimulate(); }}
              placeholder={t.sim_query_ph || 'Try: "Best SEO tools for small business"'}
              className="w-full px-6 py-4 bg-[var(--color-surface)] border-2 border-[var(--color-border)] text-white text-lg font-['JetBrains_Mono'] focus:border-[#a855f7] focus:outline-none placeholder:text-[var(--color-text-muted)]"
            />
            <input
              data-testid="simulator-url"
              type="text" value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder={t.sim_url_ph || "Your website URL (optional) — check if AI cites you"}
              className="w-full px-6 py-3 bg-[var(--color-surface)] border border-[var(--color-border)] text-white text-sm font-['JetBrains_Mono'] focus:border-[#a855f7] focus:outline-none placeholder:text-[var(--color-text-muted)]"
            />
            <button
              data-testid="simulator-submit"
              onClick={handleSimulate}
              disabled={loading || !query.trim()}
              className="w-full py-4 bg-[#a855f7] text-white font-black text-lg uppercase tracking-wider hover:bg-purple-600 transition-all disabled:opacity-50 flex items-center justify-center gap-3"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Zap className="w-5 h-5" />}
              {loading ? (t.sim_loading || "Simulating AI Responses...") : (t.sim_btn || "SIMULATE")}
            </button>
          </div>
          <p className="text-[var(--color-text-muted)] text-sm font-['JetBrains_Mono'] mt-3">{t.sim_free_note || "Free. No registration required. Instant results."}</p>
        </div>
      </section>

      {/* Results */}
      {result && (
        <section className="pb-20 px-6" data-testid="simulator-results">
          <div className="max-w-4xl mx-auto">
            {/* URL Visibility Score */}
            {url && (
              <div className="text-center mb-10 p-8 bg-[var(--color-surface)] border border-[var(--color-border)]" data-testid="visibility-score">
                <p className="text-[var(--color-text-muted)] text-sm font-['JetBrains_Mono'] uppercase tracking-wider mb-2">{t.sim_visibility || "Your AI Visibility"}</p>
                <p className={`text-6xl font-black font-['JetBrains_Mono'] ${
                  result.url_visibility >= 70 ? 'text-emerald-400' :
                  result.url_visibility >= 40 ? 'text-[#FFE600]' : 'text-[#a855f7]'
                }`}>{result.url_visibility}%</p>
                <p className="text-[var(--color-text-muted)] text-sm mt-2">{url}</p>
                {result.url_visibility < 70 && (
                  <a href="/#pricing" className="inline-flex items-center gap-2 mt-4 px-6 py-2 bg-[#FFE600] text-black text-sm font-bold hover:bg-yellow-300">
                    Improve with TopRankPulse <ArrowRight className="w-4 h-4" />
                  </a>
                )}
              </div>
            )}

            {/* Engine Responses */}
            <div className="space-y-4" data-testid="engine-responses">
              {(result.engines || []).map((eng, i) => {
                const style = ENGINE_STYLES[eng.id] || { name: eng.id, color: "#666", gradient: "from-zinc-900/40 to-zinc-950/20" };
                return (
                  <div key={i} className={`bg-gradient-to-r ${style.gradient} border border-[var(--color-border)] p-6 hover:border-[var(--color-primary)] transition-colors`} data-testid={`sim-engine-${eng.id}`}>
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <span className="text-lg font-black font-['Cabinet_Grotesk']" style={{color: style.color}}>{style.name}</span>
                        <span className="text-xs font-mono text-[var(--color-text-muted)]">{t.sim_confidence || "confidence"}: {eng.confidence}%</span>
                      </div>
                      {url && (
                        <div className="flex items-center gap-2">
                          {eng.cites_url ? (
                            <span className="flex items-center gap-1 px-3 py-1 bg-emerald-900/30 text-emerald-400 text-xs font-bold">
                              <CheckCircle className="w-3.5 h-3.5" /> CITES YOUR SITE
                            </span>
                          ) : (
                            <span className="flex items-center gap-1 px-3 py-1 bg-red-900/30 text-red-400 text-xs font-bold">
                              <X className="w-3.5 h-3.5" /> {t.sim_not_cited || "NOT CITED"}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="bg-[var(--color-bg)]/30 p-4 border-l-2" style={{borderColor: style.color}}>
                      <p className="text-[var(--color-text)] text-sm leading-relaxed italic">"{eng.response}"</p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Recommendation */}
            {result.recommendation && (
              <div className="mt-6 p-6 bg-[#FFE600]/5 border border-[#FFE600]/20" data-testid="sim-recommendation">
                <p className="text-[#FFE600] font-bold text-sm uppercase tracking-wider mb-1">{t.sim_recommendation || "Recommendation"}</p>
                <p className="text-[var(--color-text)] text-sm">{result.recommendation}</p>
              </div>
            )}

            {/* Share / CTA */}
            <div className="mt-8 text-center space-y-4">
              <p className="text-[var(--color-text-muted)] text-sm">Want to improve your AI visibility?</p>
              <div className="flex justify-center gap-4">
                <a href="/" className="px-8 py-3 bg-[#a855f7] text-white font-bold uppercase text-sm hover:bg-purple-600" data-testid="sim-scan-cta">
                  <Search className="w-4 h-4 inline mr-2" /> {t.sim_scan_cta || "Free SEO Scan"}
                </a>
                <a href="/brand-monitor" className="px-8 py-3 bg-[var(--color-surface)] border border-[var(--color-border)] text-white font-bold uppercase text-sm hover:border-zinc-500" data-testid="sim-monitor-cta">
                  <Eye className="w-4 h-4 inline mr-2" /> {t.sim_monitor_cta || "AI Brand Monitor"}
                </a>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* How it works */}
      {!result && (
        <section className="py-20 px-6 bg-[var(--color-surface)] border-y border-[var(--color-border)]">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-black font-['Cabinet_Grotesk'] tracking-tighter text-center mb-12">{t.sim_how || "HOW IT WORKS"}</h2>
            <div className="grid md:grid-cols-3 gap-8">
              {[
                { step: "01", title: t.sim_step1 || "Type a Question", desc: t.sim_step1_desc || "Enter any question your customers might ask AI search engines." },
                { step: "02", title: t.sim_step2 || "See AI Responses", desc: t.sim_step2_desc || "We simulate what ChatGPT, Perplexity & Gemini would answer." },
                { step: "03", title: t.sim_step3 || "Check Your URL", desc: t.sim_step3_desc || "Add your website to see if AI engines would cite you in their response." },
              ].map((s, i) => (
                <div key={i} className="text-center">
                  <span className="text-[#a855f7] font-['JetBrains_Mono'] text-4xl font-bold">{s.step}</span>
                  <h3 className="text-white font-bold mt-3 mb-2 font-['Cabinet_Grotesk']">{s.title}</h3>
                  <p className="text-[var(--color-text-muted)] text-sm">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <footer className="py-8 px-6 bg-[var(--color-bg)] border-t border-[var(--color-border)]">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-[#a855f7] flex items-center justify-center"><TrendingUp className="w-4 h-4 text-white" /></div>
            <span className="font-black font-['Cabinet_Grotesk']">TopRankPulse</span>
          </div>
          <p className="text-zinc-700 text-xs font-['JetBrains_Mono']">© 2026 TopRankPulse</p>
        </div>
      </footer>
    </div>
  );
}
